import { flexRender, Header, Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { IndeterminateCheckbox } from './indeterminate-checkbox';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { DataTableRow } from '../types';

interface TableHeaderProps<T extends DataTableRow> {
  table: Table<T>;
  selectable?: boolean;
  hasActions?: boolean;
}

export function DataTableHeader<T extends DataTableRow>({
  table,
  selectable = false,
  hasActions = false
}: TableHeaderProps<T>) {
  const { t } = useTranslation();

  return (
    <TableHeader>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {selectable && (
            <TableHead className="w-12">
              <IndeterminateCheckbox
                checked={table.getIsAllRowsSelected()}
                indeterminate={table.getIsSomeRowsSelected()}
                onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
                aria-label={t('common.selectAll')}
              />
            </TableHead>
          )}
          {headerGroup.headers.map((header) => (
            <TableHeaderCell key={header.id} header={header} />
          ))}
          {hasActions && <TableHead className="w-[8%]">{t('common.actions')}</TableHead>}
        </TableRow>
      ))}
    </TableHeader>
  );
}

function TableHeaderCell<T>({ header }: { header: Header<T, unknown> }) {
  const column = header.column;
  const canSort = column.getCanSort();
  const isSorted = column.getIsSorted();
  const width = (column.columnDef.meta as { width?: string })?.width;

  return (
    <TableHead
      className={`${width || ''} ${canSort ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''}`}
      onClick={canSort ? column.getToggleSortingHandler() : undefined}>
      <div className="flex items-center gap-1">
        {flexRender(header.column.columnDef.header, header.getContext())}
        {isSorted && (isSorted === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </div>
    </TableHead>
  );
}
